<script setup>
import { ref, watch } from 'vue'
import { getReservedStockUploadDetail } from '@/api/reserved-stock'
import dayjs from 'dayjs'
import { i18n } from '@/i18n'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  batch: {
    type: Object,
    default: () => ({}),
  },
  warehouseHash: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['close'])

const loading = ref(false)
const list = ref([])
const total = ref(0)
const page = ref(1)
const size = ref(20)

const columns = [
  {
    title: i18n.t('reservedStock.sku_product') || 'SKU / Product',
    key: 'product',
    width: 250,
  },
  {
    title: i18n.t('reservedStock.warehouse') || 'Warehouse',
    key: 'warehouse',
    width: 180,
  },
  {
    title: i18n.t('reservedStock.value') || 'Value',
    key: 'value',
    width: 120,
    align: 'right',
  },
  {
    title: i18n.t('reservedStock.reason_msg') || 'Reason / Message',
    key: 'reason',
    width: 300,
  },
  {
    title: i18n.t('reservedStock.status') || 'Status',
    key: 'status',
    width: 120,
    align: 'center',
  },
]

watch(() => props.visible, (val) => {
  if (val && props.batch?.process_id) {
    page.value = 1
    fetchData()
  }
})

async function fetchData() {
  loading.value = true
  try {
    const { data } = await getReservedStockUploadDetail(props.batch.process_id, {
      page: page.value - 1,
      size: size.value,
    })
    list.value = data?.content || data?.data || []
    total.value = data?.totalElements ?? data?.total_of_elements ?? data?.totalRow ?? 0
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

function handleTableChange(p) {
  page.value = p.current
  fetchData()
}
</script>

<template>
  <a-drawer
    :visible="visible"
    :title="$t('reservedStock.batch_detail_title')"
    placement="right"
    width="900"
    @close="$emit('close')"
  >
    <div v-if="batch" class="batch-header mb-4">
      <div class="row">
        <div class="col-md-4">
          <div class="label text-muted small uppercase font-weight-bold">
            {{ $t('reservedStock.upload_time') }}
          </div>
          <div class="value font-weight-bold">
            {{ dayjs(batch.started_at).format('DD MMM YYYY, HH:mm:ss') }}
          </div>
        </div>
        <div class="col-md-4">
          <div class="label text-muted small uppercase font-weight-bold">
            {{ $t('reservedStock.user') }}
          </div>
          <div class="value font-weight-bold">
            {{ batch.user_name || 'System' }}
          </div>
        </div>
        <div class="col-md-4">
          <div class="label text-muted small uppercase font-weight-bold">
            {{ $t('reservedStock.batch_status') }}
          </div>
          <a-tag :color="batch.status === 'SUCCESS' ? 'green' : 'orange'">
            {{ batch.status }}
          </a-tag>
        </div>
      </div>
    </div>

    <a-table
      :columns="columns"
      :data-source="list"
      :loading="loading"
      :pagination="{ current: page, pageSize: size, total: total, size: 'small' }"
      row-key="id"
      size="middle"
      @change="handleTableChange"
    >
      <template slot="product" slot-scope="text, record">
        <div class="font-weight-bold text-primary">
          {{ record.sku }}
        </div>
        <div class="small text-muted">
          {{ record.title }}
        </div>
      </template>

      <template slot="warehouse" slot-scope="text, record">
        {{ warehouseHash[record.warehouse_id]?.warehouse_name || record.warehouse_id || '-' }}
      </template>

      <template slot="value" slot-scope="text, record">
        <div class="d-flex flex-column align-items-end">
          <div class="text-muted small" style="text-decoration: line-through">
            {{ record.previous_reserved_stock ?? 0 }}
          </div>
          <div class="font-weight-bold" style="font-size: 14px">
            {{ record.new_reserved_stock ?? record.reserved_stock ?? 0 }}
          </div>
        </div>
      </template>

      <template slot="reason" slot-scope="text, record">
        <div class="reason-text">
          {{ record.reason || '-' }}
        </div>
        <div v-if="record.message" class="text-danger small mt-1">
          {{ record.message }}
        </div>
      </template>

      <template slot="status" slot-scope="text, record">
        <a-tag :color="record.status === 'SUCCESS' ? 'green' : (record.status === 'SKIPPED' ? 'orange' : 'red')" class="m-0 border-0 rounded-pill px-3">
          {{ record.status }}
        </a-tag>
      </template>
    </a-table>
  </a-drawer>
</template>

<style lang="scss" scoped>
.batch-header {
  background: #f8f9fb;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.reason-text {
  font-size: 12px;
  line-height: 1.4;
  color: #595959;
}

.uppercase {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
